import React, { useEffect, useState, useCallback } from 'react';
import { Megaphone, Send, Mail, MessageSquare } from 'lucide-react';
import { get, post } from '../api/client';
import { useAuth } from '../hooks/useAuth';

const CHANNELS = ['email', 'sms'];
const AUDIENCES = ['all', 'active', 'prospect'];

const STATUS_STYLES = {
  draft: 'bg-amber-100 text-amber-700',
  sent: 'bg-green-100 text-green-700',
};

const EMPTY = {
  subject: '',
  body: '',
  channel: 'email',
  audience: 'all',
};

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  'w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500';

export default function Comms() {
  const { isViewer } = useAuth();
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [sendingId, setSendingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await get('/api/v1/comms/broadcasts');
      setBroadcasts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const createDraft = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      await post('/api/v1/comms/broadcasts', form);
      setForm(EMPTY);
      load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const send = async (b) => {
    if (!window.confirm(`Send "${b.subject}" to the "${b.audience}" audience via ${b.channel}?`)) return;
    setSendingId(b.id);
    try {
      await post(`/api/v1/comms/broadcasts/${b.id}/send`);
      load();
    } catch (err) {
      alert(err.message);
    } finally {
      setSendingId(null);
    }
  };

  const channelIcon = (channel) =>
    channel === 'sms' ? <MessageSquare size={14} /> : <Mail size={14} />;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Megaphone size={20} /> Comms
          </h2>
          {isViewer && (
            <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 rounded-full px-2 py-0.5">
              Read-only demo
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2 mb-3">
          {error}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Compose */}
        {!isViewer && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-3">Compose broadcast</h3>
            <form onSubmit={createDraft} className="space-y-3">
              {formError && (
                <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2">
                  {formError}
                </div>
              )}
              <Field label="Subject">
                <input
                  required
                  className={inputCls}
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                />
              </Field>
              <Field label="Message">
                <textarea
                  required
                  rows={5}
                  className={inputCls}
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Channel">
                  <select
                    className={inputCls}
                    value={form.channel}
                    onChange={(e) => setForm({ ...form, channel: e.target.value })}
                  >
                    {CHANNELS.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Audience">
                  <select
                    className={inputCls}
                    value={form.audience}
                    onChange={(e) => setForm({ ...form, audience: e.target.value })}
                  >
                    {AUDIENCES.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg px-4 py-2"
                >
                  {saving ? 'Saving…' : 'Save draft'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* History */}
        <div className={isViewer ? 'lg:col-span-2' : ''}>
          <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-3">History</h3>
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-slate-400">Loading…</div>
            ) : broadcasts.length === 0 ? (
              <div className="p-10 text-center text-slate-400 flex flex-col items-center gap-2">
                <Megaphone size={28} />
                No broadcasts yet.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-700">
                {broadcasts.map((b) => (
                  <li key={b.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800 dark:text-slate-100 truncate">
                            {b.subject}
                          </span>
                          <span
                            className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                              STATUS_STYLES[b.status] || 'bg-slate-100'
                            }`}
                          >
                            {b.status}
                          </span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                          <span className="inline-flex items-center gap-1">
                            {channelIcon(b.channel)} {b.channel}
                          </span>
                          <span>Audience: {b.audience}</span>
                          {b.status === 'sent' && (
                            <span>{b.recipient_count} recipients</span>
                          )}
                        </div>
                      </div>
                      {!isViewer && b.status === 'draft' && (
                        <button
                          onClick={() => send(b)}
                          disabled={sendingId === b.id}
                          className="shrink-0 bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-xs font-medium rounded-lg px-3 py-1.5 flex items-center gap-1"
                          title="Send broadcast"
                        >
                          <Send size={14} /> {sendingId === b.id ? 'Sending…' : 'Send'}
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
